import { useState, useRef, useEffect } from 'react';
import { MessageSquare, X, Send, Sparkles, Bot, User } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../contexts/ThemeContext';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export default function AIChat() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: "Hi! I'm Vansh's AI assistant. Ask me anything about his projects, skills, or experience." }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const apiHistory = messages.map(m => ({ role: m.role, content: m.content }));

      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          history: apiHistory
        }),
      });

      const data = await response.json();

      if (data.response) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      } else {
        throw new Error('No response from AI');
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { role: 'assistant', content: "Sorry, I encountered an error. Please try again later." }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Theme-aware colors
  const colors = {
    // Container background with blur
    containerBg: isDark
      ? 'rgba(18, 18, 25, 0.85)'
      : 'rgba(247, 245, 239, 0.92)',
    // Subtle section backgrounds (header/footer) - very subtle difference
    sectionBg: isDark
      ? 'rgba(255, 255, 255, 0.03)'
      : 'rgba(0, 0, 0, 0.02)',
    // Border color
    border: isDark
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.06)',
    // Text colors
    text: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(30, 30, 30, 0.9)',
    textMuted: isDark ? 'rgba(255, 255, 255, 0.5)' : 'rgba(30, 30, 30, 0.5)',
    // Input background
    inputBg: isDark
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.03)',
    // Message bubbles
    assistantBubble: isDark
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.04)',
  };

  return (
    <>
      {/* Chat Trigger Button */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, duration: 0.3 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-[60] p-4 rounded-full shadow-2xl backdrop-blur-md border border-white/20 transition-all duration-300 hover:scale-110 group
          ${isOpen ? 'bg-[var(--color-uf-orange)] rotate-90 hidden sm:flex' : 'bg-[var(--color-uf-blue)] hover:bg-[var(--color-uf-blue)]/90'}`}
        aria-label="Toggle Chat"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageSquare className="w-6 h-6 text-white group-hover:animate-pulse" />
        )}
      </motion.button>

      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-0 right-0 w-full sm:w-[400px] sm:bottom-24 sm:right-6 z-50 h-[100dvh] sm:h-[500px] sm:max-h-[80vh]"
          >
            {/* Main Container - Unified background with blur */}
            <div
              className="flex flex-col h-full overflow-hidden rounded-none sm:rounded-3xl shadow-2xl backdrop-blur-xl"
              style={{
                backgroundColor: colors.containerBg,
                border: `1px solid ${colors.border}`,
              }}
            >
              {/* Header */}
              <div
                className="p-4 flex items-center justify-between"
                style={{
                  backgroundColor: colors.sectionBg,
                  borderBottom: `1px solid ${colors.border}`,
                }}
              >
                <div className="flex items-center gap-3">
                  <div
                    className="p-2 rounded-full"
                    style={{
                      backgroundColor: 'rgba(0, 82, 147, 0.15)',
                      border: '1px solid rgba(0, 82, 147, 0.25)',
                    }}
                  >
                    <Bot className="w-5 h-5 text-[var(--color-uf-blue)]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-sm" style={{ color: colors.text }}>
                      Vansh AI
                    </h3>
                    <p className="text-xs flex items-center gap-1" style={{ color: colors.textMuted }}>
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                      Online
                    </p>
                  </div>
                </div>
                {/* Close button for mobile */}
                <button
                  onClick={() => setIsOpen(false)}
                  className="sm:hidden p-2 rounded-xl transition-colors"
                  style={{
                    backgroundColor: colors.inputBg,
                  }}
                  aria-label="Close chat"
                >
                  <X className="w-5 h-5" style={{ color: colors.text }} />
                </button>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
                {messages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                  >
                    <div
                      className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: msg.role === 'assistant'
                          ? 'rgba(0, 82, 147, 0.15)'
                          : 'rgba(250, 70, 22, 0.15)',
                        border: `1px solid ${msg.role === 'assistant'
                          ? 'rgba(0, 82, 147, 0.25)'
                          : 'rgba(250, 70, 22, 0.25)'}`,
                      }}
                    >
                      {msg.role === 'assistant'
                        ? <Sparkles className="w-4 h-4 text-[var(--color-uf-blue)]" />
                        : <User className="w-4 h-4 text-[var(--color-uf-orange)]" />}
                    </div>

                    <div
                      className={`max-w-[75%] p-3 rounded-2xl text-sm leading-relaxed
                        ${msg.role === 'assistant' ? 'rounded-tl-none' : 'rounded-tr-none'}`}
                      style={{
                        backgroundColor: msg.role === 'assistant'
                          ? colors.assistantBubble
                          : 'var(--color-uf-blue)',
                        color: msg.role === 'assistant' ? colors.text : 'white',
                        border: msg.role === 'assistant' ? `1px solid ${colors.border}` : 'none',
                        boxShadow: msg.role === 'user' ? '0 4px 12px rgba(0, 82, 147, 0.2)' : 'none',
                      }}
                    >
                      {msg.content}
                    </div>
                  </div>
                ))}
                {isLoading && (
                  <div className="flex gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: 'rgba(0, 82, 147, 0.15)',
                        border: '1px solid rgba(0, 82, 147, 0.25)',
                      }}
                    >
                      <Bot className="w-4 h-4 text-[var(--color-uf-blue)]" />
                    </div>
                    <div
                      className="p-3 rounded-2xl rounded-tl-none flex items-center gap-1"
                      style={{
                        backgroundColor: colors.assistantBubble,
                        border: `1px solid ${colors.border}`,
                      }}
                    >
                      <span
                        className="w-1.5 h-1.5 rounded-full animate-bounce"
                        style={{ backgroundColor: colors.textMuted, animationDelay: '0ms' }}
                      />
                      <span
                        className="w-1.5 h-1.5 rounded-full animate-bounce"
                        style={{ backgroundColor: colors.textMuted, animationDelay: '150ms' }}
                      />
                      <span
                        className="w-1.5 h-1.5 rounded-full animate-bounce"
                        style={{ backgroundColor: colors.textMuted, animationDelay: '300ms' }}
                      />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div
                className="p-4"
                style={{
                  backgroundColor: colors.sectionBg,
                  borderTop: `1px solid ${colors.border}`,
                }}
              >
                <form onSubmit={handleSubmit} className="relative">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Ask about Vansh's resume..."
                    className="w-full rounded-xl pl-4 pr-12 py-3 text-sm transition-all focus:outline-none"
                    style={{
                      backgroundColor: colors.inputBg,
                      border: `1px solid ${colors.border}`,
                      color: colors.text,
                    }}
                  />
                  <button
                    type="submit"
                    disabled={!input.trim() || isLoading}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg bg-[var(--color-uf-blue)] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--color-uf-blue)]/80 transition-colors"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
